import { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { reduxForm, reset, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Reports from '../../../constants/reports';
import ReportsAPI from '../../../api/report';
import ReportForm, { GenerateReportButton } from '../ReportForm';
import { ReportMUIPriceField } from '../ReportFormFields';
import ReportHelper from '../../../helpers/report/ReportHelper';
import Commons from '../../../helpers/Commons';
import { entryPoints } from '../../../constants/entryPoint';
import Colors from '../../../constants/colors';
import useAmountValidation from '../../../hooks/useAmountValidation';
import CESPreviewModal from '../dppr/CESPreviewModal';
import { NOT_DIGIT } from '../../../constants/regexPatterns';
import { trackEvent } from '../../../actions/segment';
import SegmentHelper from '../../../helpers/Segment';
import SEGMENT from '../../../constants/segment';
import { routeCodes } from '../../../constants/routes';

const selector = formValueSelector('equityReportForm');
const MAX_AMOUNT = 2100000000; // 2.1 billion
const ACKNOWLEDGE_AND_AGREE = 'I acknowledge and agree that I will first obtain my client\'s consent to receive this report before supplying them with the Equity Calculator Report.';

const EquityReport = ({
    toggleReportModal,
    clapiUsrDetail,
    propertyId,
    propertyAddress,
    suburbAddress,
    route,
    propertyState,
}) => {
    const dispatch = useDispatch();
    const { amount, setAmount, amountError, validateAmount } = useAmountValidation(MAX_AMOUNT);
    const [acknowledge, setAcknowledge] = useState(false);
    const [equityReport, setEquityReport] = useState({});
    const [error, setError] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    const { email, phoneNumber } = ReportHelper.getUserContactDetail(clapiUsrDetail);
    const username = `${Commons.get(clapiUsrDetail, 'firstName')} ${Commons.get(clapiUsrDetail, 'lastName')}`
    const isVIC = propertyState?.toLowerCase() === 'vic';
    const entryPoint = route.id === routeCodes.PROPERTY.id ?
        entryPoints.REPORTS_MENU :
        entryPoints[route.id].entryPoint;

    const resetForm = () => {
        dispatch(reset('equityReportForm'));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') e.preventDefault();
    };

    const renderFields = () => (
        <div>
            <div className="form-group container-divider">
                <p>Edit Report Details</p>
            </div>
            <hr />
            <ReportMUIPriceField
                name="currentLoanBalance"
                id="current-lease-amount"
                placeholder="0"
                maxLength="10"
                label="Current Loan Balance"
                autoFocus
                customClass="equity-report-field"
                setValue={setAmount}
                handleKeyPress={handleKeyPress}
                valueError={amountError}
                value={amount}
            />
            {
                isVIC &&
                <FormControlLabel
                    checked={acknowledge}
                    onChange={() => setAcknowledge(!acknowledge)}
                    control={<Checkbox
                        id="acknowledge-and-agree"
                        color="secondary"
                    />}
                    label={ACKNOWLEDGE_AND_AGREE}
                    sx={{
                        display: 'inline-block',
                        margin: '0 0 32px 0',
                        '& .MuiCheckbox-root': {
                            padding: 0,
                            marginRight: '9px',
                        },
                        '& .MuiTypography-root': {
                            color: Colors.BLACK_60_OPAQUE,
                            fontSize: '13px',
                            textAlign: 'justify',
                        }
                    }}
                />
            }
        </div>
    );

    const handleResetForm = () => {
        toggleReportModal();
        resetForm();
    };

    const generateEquityReport = async (e, isRetry) => {
        const { pageContext } = route;
        const validAmount = validateAmount();

        if (validAmount) {
            setOpenModal(true);
            setError(false);
            if (!isRetry) {
                dispatch(trackEvent(SegmentHelper.generateReport({
                    pageContext,
                    entryPoint,
                    propertyId,
                    reportType: SEGMENT.EVENT_PROPERTIES.EQUITY_CALCULATOR_REPORT,
                    reportFormat: SEGMENT.EVENT_PROPERTIES.HTML,
                })));
            }
            const requestBody = {
                propertyId,
                loanBalance: amount && amount !== '0'
                    && parseInt(amount.replace(NOT_DIGIT, '')),
                preparedBy: username,
                email,
                phone: phoneNumber,
            }

            try {
                const response = await ReportsAPI.getEquityReport(requestBody);
                setEquityReport(response);
            } catch (error) {
                console.error('Error generating equity calculator report:', error);
                setError(true);
            }
        }

        return validAmount;
    };

    return (
        <div>
            {
                !openModal &&
                <ReportForm
                    propertyId={propertyId}
                    icon="equity_report_white.svg"
                    title="Equity Calculator Report"
                    fields={renderFields()}
                    toggleReportModal={handleResetForm}
                    reportType={Reports.SUBURB_REPORT}
                    reportName={suburbAddress}
                    filename={undefined}
                    entryPoint={entryPoints[route.id].genericEntryPoint}
                    renderPdfViewer={false}
                >
                    <GenerateReportButton
                        onClick={generateEquityReport}
                        disabled={
                            !!amountError ||
                            (isVIC ? !acknowledge : false)
                        }
                    />
                </ReportForm>
            }

            <CESPreviewModal
                open={openModal}
                cesLink={equityReport?.reportLink}
                cesLinkExpiry={equityReport?.expirationDate}
                propertyAddress={propertyAddress}
                onRetry={(e) => generateEquityReport(e, true)}
                propertyId={propertyId}
                onClose={toggleReportModal}
                dispatch={dispatch}
                withError={error}
                entryPoint={entryPoint}
                type={Reports.CES_REPORT_TYPES.equityCalculatorReport}
            />
        </div>
    );
};

EquityReport.propTypes = {
    toggleReportModal: PropTypes.func,
    clapiUsrDetail: PropTypes.object,
    propertyId: PropTypes.number,
    propertyAddress: PropTypes.string,
    suburbAddress: PropTypes.string,
    route: PropTypes.object,
    preparedFor: PropTypes.string,
};

const mapStateToProps = (state) => ({
    preparedFor: selector(state, 'preparedFor'),
    clapiUsrDetail: state.clapi.get('usrDetail'),
});

const ConnectedEquityReport = reduxForm({
    form: 'equityReportForm',
    initialValues: {
        preparedFor: '',
    },
    destroyOnUnmount: true,
})(EquityReport);

ConnectedEquityReport.displayName = 'EquityReport';

export default connect(mapStateToProps)(ConnectedEquityReport);